import {
  DIDOMI_READY_TIMEOUT_MS,
  injectDidomiLoader,
  onDidomiReady,
} from '@cda/tracking';

import i18n from '@/i18n';
import { loadGtmContainer } from '@/plugins/gtmPlugin';

/**
 * Injects the Didomi consent notice, then loads the GTM container behind it.
 *
 * Google Consent Mode is order-dependent: the container must boot **after** the
 * CMP has published its default consent values. `gtmPlugin` therefore installs
 * with `loadScript: false` and this function owns the handoff — Didomi publishes
 * its defaults during its own bootstrap, before `didomiOnReady` drains, so that
 * queue is the earliest point at which GTM can safely load.
 *
 * Rolled out per brand and per environment through `VITE_APP_DIDOMI_API_KEY`:
 * with no key there is no notice to wait for and GTM loads immediately, which is
 * exactly the behaviour this app had before. The customer space gates the same
 * SDK on the `didomiCmp` Remote Config flag instead (see its `useCookieConsent`)
 * — this app has no Remote Config, and the env var is the switch that already
 * exists here.
 *
 * Call once the router has settled: the notice has to render in the language the
 * app resolved (`/nl/...` on the shared Belgian domain), and that only happens
 * in the router's `beforeEach` guard.
 */
export const initCookieConsent = () => {
  const apiKey = import.meta.env.VITE_APP_DIDOMI_API_KEY as string | undefined;
  const noticeId = import.meta.env.VITE_APP_DIDOMI_NOTICE_ID as
    | string
    | undefined;

  if (apiKey) {
    injectDidomiLoader({
      apiKey,
      noticeId,
      locale: i18n.global.locale.value,
    });
  }

  const timeout = window.setTimeout(loadGtmContainer, DIDOMI_READY_TIMEOUT_MS);

  const queued = onDidomiReady(() => {
    window.clearTimeout(timeout);
    loadGtmContainer();
  });

  if (queued) return;

  // No loader in the DOM (no key configured, or the injection failed), so
  // nothing will ever drain the queue.
  window.clearTimeout(timeout);
  loadGtmContainer();
};
